import React from 'react';
import {Box, Chip, Typography} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import OperateurAvatar from "./OperateurAvatar.js";
import {formatMinutes, getStatutGlobal, minutesToDecimal} from "./validationHelpers.js";

// Dots représentant l'état de validation de chaque semaine
function SemaineDots({semaines}) {
    return (
        <Box display="flex" gap={0.5} mt={0.75}>
            {(semaines || []).map((s, i) => (
                <Box key={i} sx={{
                    width: 14, height: 4, borderRadius: 2,
                    backgroundColor: s.statutValidation === "valide" ? "#1D9E75" : "#ccc",
                }}/>
            ))}
        </Box>
    );
}

// Badge des heures supplémentaires (orange si > 20h, vert sinon)
function HSBadge({minutes}) {
    if (!minutes || minutes <= 0) return null;
    const h = minutesToDecimal(minutes);
    const isHigh = h > 20;
    return (
        <Chip
            label={`+${h}h HS`}
            size="small"
            color={isHigh ? "warning" : "success"}
        />
    );
}

// Liste des opérateurs (colonne de gauche)
export default function OperateurList({operateurs, selectedOperateurId, onSelect}) {
    if (!operateurs || operateurs.length === 0) {
        return (
            <Box p={2}><Typography variant="body2" color="textSecondary">Aucun opérateur.</Typography></Box>
        );
    }

    const nbValides = operateurs.filter(o => getStatutGlobal(o.semaines) === "valide").length;

    return (
        <>
            {/* Compteur de progression de la file */}
            <Box sx={{
                px: 2, py: 1,
                position: "sticky", top: 0, zIndex: 1,
                backgroundColor: "#f7f7f8",
                borderBottom: "1px solid #e0e0e0",
            }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {nbValides}/{operateurs.length} opérateur{operateurs.length > 1 ? "s" : ""} validé{nbValides > 1 ? "s" : ""}
                </Typography>
                <Box sx={{mt: 0.5, height: 4, borderRadius: 2, backgroundColor: "#e0e0e0", overflow: "hidden"}}>
                    <Box sx={{
                        width: `${operateurs.length ? (nbValides / operateurs.length) * 100 : 0}%`,
                        height: "100%", backgroundColor: "#1D9E75",
                        transition: "width 0.3s ease",
                    }}/>
                </Box>
            </Box>

            {operateurs.map(o => {
                const isSelected = o.operateur.id === selectedOperateurId;
                const ot = o.operateur.overtimeMinutes || 0;
                const isAllValidated = getStatutGlobal(o.semaines) === "valide";
                const nbAlertes = (o.semaines || []).reduce((acc, s) => acc + (s.alertes?.length || 0), 0);

                return (
                    <Box
                        key={o.operateur.id}
                        onClick={() => onSelect(o.operateur.id)}
                        sx={{
                            px: 2,
                            py: 1.5,
                            cursor: "pointer",
                            borderBottom: "1px solid #ebebeb",
                            backgroundColor: isSelected ? "#fff" : "transparent",
                            borderLeft: isAllValidated ? "3px solid #1D9E75" : isSelected ? "3px solid #7F77DD" : "3px solid transparent",
                            "&:hover": {backgroundColor: "#fff"},
                            display: "flex", alignItems: "flex-start", gap: 1.5,
                        }}
                    >
                        <OperateurAvatar name={o.operateur.libelle}/>
                        <Box flex={1} minWidth={0}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <Typography variant="body1" fontWeight={600}>
                                    {o.operateur.libelle}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    {nbAlertes > 0 && (
                                        <Chip
                                            icon={<WarningAmberIcon/>}
                                            label={nbAlertes}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            title={`${nbAlertes} alerte(s) à contrôler`}
                                        />
                                    )}
                                    <HSBadge minutes={ot}/>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {formatMinutes(o.operateur.totalMinutes)}
                            </Typography>
                            <SemaineDots semaines={o.semaines}/>
                        </Box>
                    </Box>
                );
            })}
        </>
    );
}
